import { ExclamationIcon, MailIcon } from "@heroicons/react/outline";
import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PendingWorkspaceInvites } from "./PendingWorkspaceInvites";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth-context";
import { useNoWorkspaceAccessContext } from "@/queries/identity/useNoWorkspaceAccessContext";
import { useGetWorkspaceInvites } from "@/queries/workspace";

/**
 * Landing page shown after SSO sign-in when the just-provisioned user
 * has no workspace access yet. Surfaces pending workspace invites inline
 * so JIT users can accept without reaching the main app shell. Reads the
 * Owner contact from URL params on first paint and falls back to the
 * authenticated context endpoint on refresh; auto-bounces to `/home` once
 * access is restored.
 */
export const NoWorkspaceAccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const { data, isLoading } = useNoWorkspaceAccessContext();
  const userEmail = auth?.user?.email;

  const { data: invitesData, isLoading: invitesLoading } = useGetWorkspaceInvites(
    userEmail ? { invitee_email: userEmail, status: "pending" } : undefined,
  );

  const pendingInvites = useMemo(
    () => (invitesData?.items ?? []).filter(invite => !invite.is_expired),
    [invitesData],
  );
  const hasPendingInvites = pendingInvites.length > 0;

  const organizationName = data?.organization_name ?? searchParams.get("org") ?? "";
  const ownerEmail = data?.owner_email ?? searchParams.get("email") ?? "";
  const ownerName = data?.owner_name ?? searchParams.get("name") ?? "";

  useEffect(() => {
    if (data?.has_workspace_access) {
      navigate("/home", { replace: true });
    }
  }, [data?.has_workspace_access, navigate]);

  const handleSignOut = () => {
    auth?.logout();
    navigate("/login");
  };

  const mailtoHref = ownerEmail
    ? `mailto:${ownerEmail}?subject=${encodeURIComponent(
        `PromptLayer access for ${organizationName || "our team"}`,
      )}`
    : undefined;

  const statusMessage = (() => {
    if (hasPendingInvites) {
      return (
        <>
          Your account is ready under{" "}
          {organizationName ? (
            <span className="font-semibold text-gray-900">{organizationName}</span>
          ) : (
            "your organization"
          )}
          . Accept a workspace invitation below to get started.
        </>
      );
    }
    if (organizationName) {
      return (
        <>
          Your account has been provisioned under{" "}
          <span className="font-semibold text-gray-900">{organizationName}</span>, but no workspace
          access has been granted yet. Ask your administrator to add you to a workspace.
        </>
      );
    }
    return (
      <>
        Your account has been provisioned, but no workspace access has been granted yet. Ask your
        administrator to add you to a workspace.
      </>
    );
  })();

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
        <ExclamationIcon className="h-8 w-8 text-amber-600" strokeWidth={2} />
      </div>

      <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">No workspace access yet</h1>

      <p className="mb-8 text-base text-gray-600">{statusMessage}</p>

      {invitesLoading && userEmail && (
        <div className="mb-6 flex w-full justify-center py-2">
          <LoadingSpinner size={5} />
        </div>
      )}

      {hasPendingInvites && (
        <div className="mb-6 w-full">
          <PendingWorkspaceInvites invites={pendingInvites} />
        </div>
      )}

      {(ownerEmail || isLoading) && (
        <div className="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Contact your administrator
          </p>
          {isLoading && !ownerEmail ? (
            <div className="flex py-1">
              <LoadingSpinner size={4} />
            </div>
          ) : (
            <a
              href={mailtoHref}
              className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <MailIcon className="h-4 w-4" />
              {ownerName ? `${ownerName} — ${ownerEmail}` : ownerEmail}
            </a>
          )}
        </div>
      )}

      <div className="mt-8 flex w-full flex-col gap-3">
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
        <Button variant="ghost" onClick={handleSignOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
};
